import { useMemo, useState } from "react";
import { AxiosResponse } from "axios";
import {
  deleteThread as deleteThreadAPIFunction,
  GetThreadRequest,
  getThreadRequestURI,
  ListThreadMessagesRequest,
  listThreadMessagesRequestURI,
  ListThreadMessagesResponse,
  listThreadUsersRequestURI,
  ListThreadUsersResponse,
} from "duck/endpoints/threads/api";
import { Thread } from "duck/ui/chat/types";
import { mutate } from "swr";

import { DeleteRequest, getPydanticAIFetcher } from "shared/api/api";
import { useAPI } from "shared/api/hooks";

import {
  createThread,
  ListThreadsRequest,
  listThreadsRequestURI,
  ListThreadsResponse,
  updateThreadTitle,
} from "./api";
import {
  getStoredThreadId,
  getThreadFromThreads,
  setStoredThreadId,
  threadNeedsTitleUpdate,
} from "./utils";

const useListThreads = (args: ListThreadsRequest) =>
  useAPI<typeof args, ListThreadsResponse>(
    listThreadsRequestURI,
    args,
    undefined,
    getPydanticAIFetcher
  );

export const useListThreadMessages = (
  args: ListThreadMessagesRequest,
  swrOptions?: Parameters<typeof useAPI>[2]
) =>
  useAPI<ListThreadMessagesRequest, ListThreadMessagesResponse>(
    listThreadMessagesRequestURI,
    args,
    swrOptions,
    getPydanticAIFetcher
  );

export const useThread = (
  args: GetThreadRequest,
  swrOptions?: Parameters<typeof useAPI>[2]
) =>
  useAPI<GetThreadRequest, Thread>(
    getThreadRequestURI,
    args,
    swrOptions,
    getPydanticAIFetcher
  );

export const useListThreadUsers = () =>
  useAPI<undefined, ListThreadUsersResponse>(
    listThreadUsersRequestURI,
    undefined,
    undefined,
    getPydanticAIFetcher
  );

interface UseThreadManagerResponse {
  currentThreadId: string | null;
  currentThreadMessages: ListThreadMessagesResponse | null;
  isLoadingCurrentThread: boolean;
  isCreating: boolean;
  isSwitchingThread: boolean;
  createNewThread: () => Promise<string>;
  threads: Thread[] | null;
  isLoadingThreads: boolean;
  selectThread: (threadId: string, refetchThreads: boolean) => void;
  requestKey: string;
  listThreadMessagesRequestKey: string;
  updateCurrentThreadTitle: (threadId?: string) => Promise<void>;
  deleteThread: (args: DeleteRequest) => Promise<AxiosResponse<any, any, {}>>;
  setUseListThreadArgs: (args: ListThreadsRequest) => void;
  useListThreadArgs: ListThreadsRequest;
}

export const useThreadManager = (): UseThreadManagerResponse => {
  const [useListThreadArgs, setUseListThreadArgs] = useState({});
  const {
    data: threadsData,
    isLoading: isLoadingThreads,
    requestKey,
  } = useListThreads(useListThreadArgs);

  const [currentThreadId, setCurrentThreadId] = useState<string | null>(() =>
    getStoredThreadId()
  );
  const [isCreating, setIsCreating] = useState(false);
  // Counts selectThread calls still reloading the list, so an earlier reload
  // cannot end the switch while a later one is still running.
  const [pendingSwitches, setPendingSwitches] = useState(0);
  const isSwitchingThread = pendingSwitches > 0;

  /**
   * Current thread is determined by:
   * 1. Stored thread ID (if it exists in the threads list)
   * 2. First thread in the list (if stored thread is invalid or missing)
   * 3. null (if there are no threads available).
   *
   * Note: Invalid stored thread IDs are automatically cleared from storage.
   * Note 2: new thread is created once we send the first message if null
   */
  const currentThread = useMemo(() => {
    if (!threadsData?.threads) return null;

    if (currentThreadId) {
      const found = getThreadFromThreads(threadsData.threads, currentThreadId);
      if (found) return found;

      // Don't clear stored thread ID if we're currently creating or switching threads
      // This prevents race conditions during thread creation/switching
      if (!isCreating && !isSwitchingThread) {
        console.warn(
          `Stored thread ${currentThreadId} not found, clearing from storage`
        );
        setStoredThreadId(null);
        setCurrentThreadId(null);
      } else {
        // During creation/switching, return null but keep the stored ID
        return null;
      }
    }

    // Auto-select first thread if available
    const firstThread = threadsData.threads[0] ?? null;
    if (firstThread && !currentThreadId) {
      setCurrentThreadId(firstThread.id);
      setStoredThreadId(firstThread.id);
    }

    return firstThread;
  }, [threadsData?.threads, currentThreadId, isCreating, isSwitchingThread]);

  const createNewThread = async (): Promise<string> => {
    setIsCreating(true);
    try {
      const newThread = await createThread(requestKey);
      setCurrentThreadId(newThread.id);
      setStoredThreadId(newThread.id);

      return newThread.id;
    } finally {
      setIsCreating(false);
    }
  };

  const selectThread = async (threadId: string, refetchThreads: boolean) => {
    // Allow selecting a thread ID even if the list hasn't been refreshed yet
    setPendingSwitches((count) => count + 1);
    setCurrentThreadId(threadId);
    setStoredThreadId(threadId);

    try {
      if (refetchThreads) {
        // Hold the switching state until the revalidated list arrives —
        // releasing it earlier lets currentThread treat a freshly created
        // thread (e.g. a consumed share clone) as invalid and clear it.
        await mutate(requestKey);
      }
    } finally {
      setPendingSwitches((count) => count - 1);
    }
  };

  const updateCurrentThreadTitle = async (threadId?: string): Promise<void> => {
    const idToUpdate = threadId ?? currentThreadId;
    if (!idToUpdate) return;

    // Find the thread in the list
    const thread = threadsData?.threads
      ? getThreadFromThreads(threadsData.threads, idToUpdate)
      : null;

    // If thread is not in the list yet (e.g., just created), optimistically call update
    // The backend will handle checking if update is needed
    if (!thread) {
      console.log("Thread not in list yet, optimistically updating title");
      await updateThreadTitle(idToUpdate);
      mutate(requestKey);

      return;
    }

    if (threadNeedsTitleUpdate(thread)) {
      await updateThreadTitle(thread.id);
      mutate(requestKey);
    }
  };

  const deleteThread = async (
    args: DeleteRequest
  ): Promise<AxiosResponse<any, any, {}>> => {
    const resp = await deleteThreadAPIFunction(args);
    mutate(requestKey);

    return resp;
  };

  const threadMessagesRequestParams: ListThreadMessagesRequest = {
    threadId: currentThreadId ?? "",
    skipRequest: !currentThread,
  };

  const {
    data: currentThreadMessagesData,
    requestKey: listThreadMessagesRequestKey,
    isLoading: isLoadingCurrentThreadMessages,
  } = useListThreadMessages(threadMessagesRequestParams);

  const currentThreadMessages = currentThreadMessagesData ?? null;

  // we consider loading to be true in these cases:
  // 1. threads are still loading (we don't know which thread to load messages for yet), OR
  // 2. we have a current thread but messages are still loading
  const isLoadingCurrentThread =
    isLoadingThreads ||
    (Boolean(currentThread) && isLoadingCurrentThreadMessages);

  return {
    currentThreadId,
    currentThreadMessages,
    isLoadingCurrentThread,
    isCreating,
    isSwitchingThread,
    threads: threadsData?.threads ?? null,
    isLoadingThreads,
    createNewThread,
    selectThread,
    requestKey,
    listThreadMessagesRequestKey,
    updateCurrentThreadTitle,
    deleteThread,
    setUseListThreadArgs,
    useListThreadArgs,
  };
};
